import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { PublicReadUnavailable, fetchPublicRead } from "@/lib/public-read";
import type { Json } from "@/integrations/supabase/types";
import { fetchUserDataBatch } from "@/lib/user-data-helpers";
import { getTopicGenerationState } from "@/lib/topic-generation-state";
import { db } from "@lyder-talk/core/lib/db-client";
import { apiGet } from "@lyder-talk/core/lib/api-client";

export type PublicSession = {
  id: string;
  user_id: string;
  created_at: string;
  topic: string;
  slug: string;
  questions: Json;
  vocabulary: Json;
  category?: string | null;
  banner_url?: string | null;
  creator_email?: string;
  creator_display_name?: string;
  creator_avatar_url?: string | null;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  bookmark_count?: number;
};

export type SortBy = "recent" | "popular" | "trending" | "bookmarked";

export type UsePublicSessionsParams = {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  category?: string;
  sortBy?: SortBy;
  enabled?: boolean;
};

export type UsePublicSessionsResult = {
  data: PublicSession[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

// Type for server-side sorted session result
interface ServerSortedSession {
  id: string;
  user_id: string;
  created_at: string;
  topic: string;
  slug: string;
  banner_url: string | null;
  // RPCs now return counts instead of full questions/vocabulary JSONB.
  question_count: number;
  vocabulary_count: number;
  category_id: string | null;
  category_name: string | null;
  view_count: number;
  like_count: number;
  comment_count: number;
  bookmark_count: number;
  trending_score?: number;
}

export const usePublicSessions = (params: UsePublicSessionsParams = {}) => {
  const { page = 1, pageSize = 16, searchTerm = "", category, sortBy = "recent", enabled = true } = params;

  return useQuery({
    queryKey: queryKeys.publicSessions.list(page, pageSize, searchTerm, category, sortBy),
    enabled,
    /**
     * One retry, not the default three.
     *
     * Browse shows a skeleton, not an error, while the query is pending — so
     * every retry the default adds is another second of "still loading" before
     * the page can admit the API is unreachable. Measured: 9s of skeleton and
     * then the error panel. A list read that fails twice will not succeed on the
     * third attempt; one retry covers the transient blip.
     */
    retry: 1,
    queryFn: async (): Promise<UsePublicSessionsResult> => {
      // Get category_id if category filter is provided
      let categoryId: string | null = null;
      if (category && category !== "All") {
        const { data: categoryData } = await db.from("categories")
          .select("id")
          .eq("name", category)
          .single();

        if (categoryData?.id) {
          categoryId = categoryData.id;
        }
      }

      // Use server-side sorting RPC functions for trending, popular, and bookmarked
      if (sortBy === "trending" || sortBy === "popular" || sortBy === "bookmarked") {
        return await fetchWithServerSideSort(sortBy, page, pageSize, categoryId, searchTerm);
      }

      // For "recent" sort, use the standard query approach
      return await fetchRecentSessions(page, pageSize, categoryId, searchTerm);
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Rows for one page of the sorted (RPC) path, plus the pager total.
 *
 * Both acquisition strategies return this shape so the post-processing below is
 * written once: the cached `public-read` endpoint first, the direct RPC pair
 * second.
 */
interface SortedPage {
  sessions: ServerSortedSession[];
  total: number;
}

/** Direct fallback for the sorted path — used when the cached read is unavailable. */
async function fetchSortedDirect(
  sortBy: "trending" | "popular" | "bookmarked",
  page: number,
  pageSize: number,
  categoryId: string | null,
  searchTerm: string,
): Promise<SortedPage> {
  const offset = (page - 1) * pageSize;

  // A sort key, not a function name: the server owns the mapping.
  const browseQuery = new URLSearchParams({
    sort: sortBy,
    limit: String(pageSize),
    offset: String(offset),
  });
  if (categoryId) browseQuery.set("categoryId", categoryId);

  const sessionsData = await apiGet<ServerSortedSession[]>(`/v1/sessions/browse?${browseQuery.toString()}`);
  const error = null;

  if (error) throw error;

  // Get total count for pagination — inner joins require at least one question
  // and one vocabulary row, i.e. a topic whose generation ran to completion.
  // `!inner` is the whole filter: a `.not("questions", "is", null)` beside it is
  // a PostgREST relation filter the gateway rejects (it names no column), and it
  // was the reason this query answered 400 and the pager read "0".
  let countQuery = db.from("practice_sessions")
    .select("id, questions!inner(id), vocabulary!inner(id)", { count: "exact", head: true })
    .eq("is_public", true);

  if (categoryId) {
    countQuery = countQuery.eq("category_id", categoryId);
  }

  if (searchTerm.trim()) {
    countQuery = countQuery.ilike("topic", `%${searchTerm.trim()}%`);
  }

  const { count } = await countQuery;

  return {
    sessions: (sessionsData || []) as ServerSortedSession[],
    total: count || 0,
  };
}

/**
 * Fetch sessions using server-side sorting RPC functions
 * Uses denormalized counts from practice_sessions - no extra table fetches needed
 */
async function fetchWithServerSideSort(
  sortBy: "trending" | "popular" | "bookmarked",
  page: number,
  pageSize: number,
  categoryId: string | null,
  searchTerm: string,
): Promise<UsePublicSessionsResult> {
  let pageResult: SortedPage;

  try {
    const cached = await fetchPublicRead<{ data: ServerSortedSession[]; count: number }>("topics", {
      page,
      pageSize,
      sortBy,
      categoryId,
      searchTerm: searchTerm.trim() || null,
    });
    pageResult = { sessions: cached.data ?? [], total: cached.count ?? 0 };
  } catch (error) {
    if (!(error instanceof PublicReadUnavailable)) throw error;
    console.warn("[topics] cached read unavailable, using direct query:", error.message);
    pageResult = await fetchSortedDirect(sortBy, page, pageSize, categoryId, searchTerm);
  }

  // Filter by search term if provided (server-side search not yet implemented in RPC)
  let sessions: ServerSortedSession[] = pageResult.sessions;
  if (searchTerm.trim()) {
    const term = searchTerm.trim().toLowerCase();
    sessions = sessions.filter((s) => s.topic.toLowerCase().includes(term));
  }

  // A topic row exists the moment it is created, and creation makes it public —
  // so a run that stopped early would otherwise be listed as an empty practice
  // session. The RPCs still return those rows, so drop them here; the count
  // query below counts only finished topics, keeping the pager honest.
  sessions = sessions.filter(
    (s) => (s.question_count ?? 0) > 0 && (s.vocabulary_count ?? 0) > 0
  );

  const total = pageResult.total;

  // Fetch user data for creator info
  const userIds = [...new Set(sessions.map((s) => s.user_id))];
  const userDataMap = await fetchUserDataBatch(userIds);

  // Transform to PublicSession format
  // Engagement counts come directly from the RPC result (from practice_sessions columns)
  const publicSessions: PublicSession[] = sessions.map((session) => {
    const userData = userDataMap.get(session.user_id);
    return {
      id: session.id,
      user_id: session.user_id,
      created_at: session.created_at,
      topic: session.topic,
      slug: session.slug,
      // Count placeholders — list cards only render `.length`.
      questions: Array.from({ length: session.question_count ?? 0 }),
      vocabulary: Array.from({ length: session.vocabulary_count ?? 0 }),
      category: session.category_name,
      banner_url: session.banner_url || null,
      creator_email: userData?.email || "Unknown",
      creator_display_name: userData?.display_name || "Unknown",
      creator_avatar_url: userData?.avatar_url || null,
      view_count: session.view_count,
      like_count: session.like_count,
      comment_count: session.comment_count,
      bookmark_count: session.bookmark_count,
    };
  });

  const totalPages = Math.ceil(total / pageSize);

  return {
    data: publicSessions,
    total,
    page,
    pageSize,
    totalPages,
  };
}

interface RecentPage {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[];
  total: number;
}

/** Direct fallback for the recent path — used when the cached read is unavailable. */
async function fetchRecentDirect(
  page: number,
  pageSize: number,
  categoryId: string | null,
  searchTerm: string,
): Promise<RecentPage> {
  // Build query. `!inner` + `not.is.null` restricts the page to topics that
  // actually have questions AND vocabulary, so an unfinished topic is never
  // listed and `count` reflects the visible set. Only ids are embedded: the
  // list needs counts, not the rows themselves.
  let query = db.from("practice_sessions")
    .select(`
      id,
      user_id,
      created_at,
      topic,
      slug,
      banner_url,
      questions!inner (id),
      vocabulary!inner (id),
      view_count,
      like_count,
      comment_count,
      bookmark_count,
      categories!category_id (
        id,
        name
      )
    `, { count: "exact" })
    .eq("is_public", true);

  if (categoryId) {
    query = query.eq("category_id", categoryId);
  }

  if (searchTerm.trim()) {
    query = query.ilike("topic", `%${searchTerm.trim()}%`);
  }

  // Apply ordering and pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.order("created_at", { ascending: false }).range(from, to);

  const { data, error, count } = await query;

  if (error) throw error;

  return {
    // `questions!inner (id)` / `vocabulary!inner (id)` aren't modelled by the
    // generated types, so rows are treated as any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rows: ((data as any[]) || []),
    total: count || 0,
  };
}

/**
 * Fetch recent sessions using standard query
 * Uses denormalized counts directly from practice_sessions - no extra table fetches needed
 */
async function fetchRecentSessions(
  page: number,
  pageSize: number,
  categoryId: string | null,
  searchTerm: string,
): Promise<UsePublicSessionsResult> {
  let pageResult: RecentPage;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cached = await fetchPublicRead<{ data: any[]; count: number }>("topics", {
      page,
      pageSize,
      sortBy: "recent",
      categoryId,
      searchTerm: searchTerm.trim() || null,
    });
    pageResult = { rows: cached.data ?? [], total: cached.count ?? 0 };
  } catch (error) {
    if (!(error instanceof PublicReadUnavailable)) throw error;
    console.warn("[topics] cached read unavailable, using direct query:", error.message);
    pageResult = await fetchRecentDirect(page, pageSize, categoryId, searchTerm);
  }

  const total = pageResult.total;

  // `questions!inner (id)` / `vocabulary!inner (id)` aren't modelled by the
  // generated types, so rows are treated as any. The embedded arrays carry one
  // row per item; the list only needs their lengths.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = pageResult.rows.filter(
    (session) =>
      !getTopicGenerationState({
        questions: session.questions,
        vocabulary: session.vocabulary,
      }).isIncomplete
  );

  // Fetch user data for creator info
  const userIds = [...new Set(rows.map((s) => s.user_id))];
  const userDataMap = await fetchUserDataBatch(userIds);

  // Transform sessions - counts come directly from practice_sessions columns
  const publicSessions: PublicSession[] = rows.map((session) => {
    const userData = userDataMap.get(session.user_id);
    return {
      id: session.id,
      user_id: session.user_id,
      created_at: session.created_at,
      topic: session.topic,
      slug: session.slug,
      // Count placeholders, matching the RPC path above — list cards only
      // render `.length` off these two.
      questions: Array.from({ length: session.questions?.length ?? 0 }),
      vocabulary: Array.from({ length: session.vocabulary?.length ?? 0 }),
      category: session?.categories?.name || null,
      banner_url: session.banner_url || null,
      creator_email: userData?.email || "Unknown",
      creator_display_name: userData?.display_name || "Unknown",
      creator_avatar_url: userData?.avatar_url || null,
      view_count: session.view_count || 0,
      like_count: session.like_count || 0,
      comment_count: session.comment_count || 0,
      bookmark_count: session.bookmark_count || 0,
    };
  });

  const totalPages = Math.ceil(total / pageSize);

  return {
    data: publicSessions,
    total,
    page,
    pageSize,
    totalPages,
  };
}
